import { Trans, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import React, { useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import type { MessageDescriptor } from '@lingui/core';
import { 
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarFooter,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenuBadge
} from '@shared/ui/sidebar';
import { Avatar, AvatarFallback, AvatarImage } from '@shared/ui/avatar';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@shared/ui/dropdown-menu';
import { Badge } from '@shared/ui/badge';
import { 
  Settings, 
  LogOut, 
  User, 
  ChevronUp,
} from 'lucide-react';
import type { UserProfile } from '@shared/types/auth';
import type { NavItem } from '@shared/lib/navigation';
import { useAuth } from '@shared/hooks/useAuth';
import { useQuery } from '@tanstack/react-query';
import { doctorOnboardingApi } from '@features/doctor/onboarding/api';
import { filesApi } from '@shared/api/files';

interface AppSidebarProps {
  user: UserProfile;
  navItems: NavItem[];
}

const getRoleDisplayName = (user: UserProfile, translate: (descriptor: MessageDescriptor) => string): string => {
  if (user.role === 'doctor' && user.organization_roles?.includes('owner')) {
    return translate({
      id: 'shared.layout.AppSidebar.role.organization-owner',
      message: 'Organization Owner',
    });
  }

  switch (user.role) {
    case 'patient':
      return translate({
        id: 'shared.layout.AppSidebar.role.patient',
        message: 'Patient',
      });
    case 'doctor':
      return translate({
        id: 'shared.layout.AppSidebar.role.doctor',
        message: 'Doctor',
      });
    case 'admin':
      return translate({
        id: 'shared.layout.AppSidebar.role.administrator',
        message: 'Administrator',
      });
    case 'super_admin':
      return translate({
        id: 'shared.layout.AppSidebar.role.super-admin',
        message: 'Super Admin',
      });
    default:
      return translate({
        id: 'shared.layout.AppSidebar.role.user',
        message: 'User',
      });
  }
};

const getRoleColor = (user: UserProfile): string => {
  if (user.role === 'doctor' && user.organization_roles?.includes('owner')) {
    return 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-200';
  }

  switch (user.role) {
    case 'patient':
      return 'bg-brand-blue/10 text-brand-blue';
    case 'doctor':
      return 'bg-brand-lime/20 text-brand-lime-foreground';
    case 'admin':
      return 'bg-brand-orange/10 text-brand-orange';
    case 'super_admin':
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300';
    default:
      return 'bg-muted text-muted-foreground';
  }
};

export const AppSidebar: React.FC<AppSidebarProps> = ({ user, navItems }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const { i18n } = useLingui();
  const isDoctor = user.role === 'doctor' && Boolean(user.as_doctor_id);

  const { data: doctorProfile } = useQuery({
    queryKey: ['doctor', 'sidebar-profile', user.as_doctor_id],
    queryFn: async () => {
      if (!user.as_doctor_id) {
        return null;
      }
      return doctorOnboardingApi.getState(user.as_doctor_id);
    },
    enabled: isDoctor,
    staleTime: 5 * 60 * 1000,
  });

  const { data: profilePhotoUrl } = useQuery({
    queryKey: ['file-url', user.profile_image],
    queryFn: async () => {
      if (!user.profile_image) {
        return undefined;
      }
      const { url } = await filesApi.getPresignedDownloadUrl({ storage_path: user.profile_image });
      return url;
    },
    enabled: Boolean(user.profile_image),
    staleTime: 5 * 60 * 1000,
  });

  const doctorSummary = useMemo(() => {
    if (!doctorProfile) {
      return null;
    }
    const preview = doctorProfile.profilePreview ?? {};
    const marketing = (doctorProfile.marketing as Record<string, unknown> | null) ?? null;

    return {
      displayName: (preview.displayName || preview.name || `${user.first_name} ${user.last_name}`).toString().trim(),
      primarySpecialty: (preview.primarySpecialty as string | undefined) || undefined,
      headline: (marketing?.headline as string | undefined) || undefined,
      contactEmail: (preview.contactEmail as string | undefined) || user.email,
      languages: (preview.languages as string[] | undefined) || undefined,
    };
  }, [doctorProfile, user.email, user.first_name, user.last_name]);

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Sign out failed:', error);
    }
  };

  const handleProfileClick = () => {
    navigate('/doctor-profile');
  };

  const getUserInitials = (name: string): string => {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) {
      return 'DR';
    }
    return parts
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join('') || 'DR';
  };

  const roleDisplay = getRoleDisplayName(user, (descriptor) => i18n._(descriptor));
  const roleColor = getRoleColor(user);
  const displayName = doctorSummary?.displayName || `${user.first_name} ${user.last_name}`.trim();
  const headline = doctorSummary?.headline;
  const profileEmail = doctorSummary?.contactEmail ?? user.email;
  const fallbackAvatar = `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(displayName)}`;
  const avatarSrc = profilePhotoUrl ?? fallbackAvatar;
  const userInitials = getUserInitials(displayName);

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <Link to="/">
                <img
                      src="/Icon_Jema_full.svg"
                      alt={t({
                        id: 'shared.layout.AppSidebar.jema-logo',
                        message: 'Jema Logo',
                      })}
                      className="w-full h-full object-contain  hover:cursor-pointer hover:bg-primary/10"
                    />
              </Link>
              {/*<Link to="/" className="flex items-center gap-2">
                <div className="flex aspect-square size-10 items-center justify-center  rounded-lg  text-primary-foreground ">
                  <img
                    src="/Icon_Jema.svg"
                    alt={t({
                      id: 'shared.layout.AppSidebar.jema-logo',

                      message: 'Jema Logo',
                    })}
                    className="size-10"
                  />
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold"><Trans id='shared.layout.AppSidebar.jema-manager'>Jema Manager</Trans></span>
                  <span className="truncate text-xs text-muted-foreground"><Trans id='shared.layout.AppSidebar.medical-tourism-platform'>Medical Tourism Platform</Trans></span>
                </div>
              </Link>*/}
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel><Trans id='shared.layout.AppSidebar.navigation'>Navigation</Trans></SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {navItems.map((item) => {
                // Check for exact match or if pathname starts with item path followed by a slash
                // This handles nested routes like /messages/:roomSlug
                const isActive = location.pathname === item.path || 
                  location.pathname.startsWith(item.path + '/');
                const Icon = item.icon;
                
                return (
                  <SidebarMenuItem key={item.key}>
                    <SidebarMenuButton 
                      asChild 
                      isActive={isActive}
                      tooltip={
                        item.description
                          ? typeof item.description === 'string'
                            ? item.description
                            : i18n._(item.description as MessageDescriptor)
                          : undefined
                      }
                    >
                      <Link to={item.path} className="flex items-center gap-2">
                        <Icon className="size-4" />
                        <span>
                          {typeof item.label === 'string' 
                            ? item.label 
                            : i18n._(item.label as MessageDescriptor)}
                        </span>
                        {item.badge && item.badge > 0 && (
                          <SidebarMenuBadge>{item.badge}</SidebarMenuBadge>
                        )}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                >
                  <Avatar className="h-8 w-8 rounded-lg">
                    <AvatarImage 
                      src={avatarSrc}
                      alt={displayName} 
                    />
                    <AvatarFallback className="rounded-lg">
                      {userInitials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">
                      {displayName}
                    </span>
                    <span className="truncate text-xs text-muted-foreground">
                      {headline || profileEmail}
                    </span>
                  </div>
                  <ChevronUp className="ml-auto size-4" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                side="bottom"
                align="end"
                sideOffset={4}
              >
                <DropdownMenuLabel className="p-0 font-normal">
                  <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                    <Avatar className="h-8 w-8 rounded-lg">
                      <AvatarImage 
                        src={avatarSrc}
                        alt={displayName} 
                      />
                      <AvatarFallback className="rounded-lg">
                        {userInitials}
                      </AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 gap-1 text-sm">
                      <span className="truncate font-semibold">
                        {displayName}
                      </span>
                      <span className="truncate text-xs text-muted-foreground">
                        {profileEmail}
                      </span>
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <div className="px-1 py-1.5">
                  <Badge 
                    variant="secondary" 
                    className={`text-xs ${roleColor}`}
                  >
                    {roleDisplay}
                  </Badge>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="gap-2" onClick={handleProfileClick}><Trans id='shared.layout.AppSidebar.profile'><User className="size-4" />Profile</Trans></DropdownMenuItem>
                <DropdownMenuItem className="gap-2"><Trans id='shared.layout.AppSidebar.settings'><Settings className="size-4" />Settings</Trans></DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  className="gap-2 text-red-600 focus:text-red-600 dark:text-red-400 dark:focus:text-red-400"
                  onClick={handleSignOut}
                ><Trans id='shared.layout.AppSidebar.sign-out'><LogOut className="size-4" />Sign out</Trans></DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
};
